import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { TeamRole, TeamInvitation } from "@/integrations/supabase/types/";
import { MailOpen, Copy, RefreshCw, Ban } from "lucide-react";
import { generateRandomString } from "@/lib/utils";

export const InvitationManagement = () => {
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(null);
  const [isAddingInvitation, setIsAddingInvitation] = useState(false);
  const [isResendingInvitation, setIsResendingInvitation] = useState(false);
  const [isCancelingInvitation, setIsCancelingInvitation] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<TeamInvitation | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [newInvitation, setNewInvitation] = useState<{
    email: string;
    role: TeamRole;
    department: string;
  }>({
    email: "",
    role: "member",
    department: "",
  });
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    getUser();
  }, []);

  const { data: invitations, refetch: refetchInvitations } = useQuery({
    queryKey: ["team-invitations"],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from("team_invitations")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load team invitations",
          variant: "destructive",
        });
        throw error;
      }
      return data as TeamInvitation[];
    },
    enabled: !!userId,
  });

  const handleAddInvitation = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "You must be logged in to send invitations",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      // Cancel any existing pending invitations for this email
      const { error: cancelError } = await supabase
        .from("team_invitations")
        .update({ status: "rejected" })
        .eq("email", newInvitation.email)
        .eq("status", "pending");
      
      if (cancelError) console.error("Error canceling old invitations:", cancelError);
      
      const token = generateRandomString(32);
      
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      const { data: invitationData, error: invitationError } = await supabase
        .from("team_invitations")
        .insert([{
          email: newInvitation.email,
          role: newInvitation.role,
          department: newInvitation.department,
          invited_by: userId,
          token,
          expires_at: expiresAt.toISOString(),
        }])
        .select();

      if (invitationError) throw invitationError;
      
      const { data: userData } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .single();
      
      let inviterName = "A team administrator";
      
      const { data: authUser } = await supabase.auth.getUser();
      if (authUser?.user?.user_metadata) {
        const { first_name, last_name } = authUser.user.user_metadata;
        if (first_name && last_name) {
          inviterName = `${first_name} ${last_name}`;
        }
      }
      
      const invitationLink = `${window.location.origin}/invitation?token=${token}`;
      
      await supabase.functions.invoke("send-team-invitation", {
        body: { 
          email: newInvitation.email, 
          invitationLink, 
          role: newInvitation.role,
          inviterName,
          companyName: "Spotlight Adz"
        },
      });

      toast({
        title: "Success",
        description: "Invitation sent successfully",
      });

      setIsAddingInvitation(false);
      setNewInvitation({
        email: "",
        role: "member",
        department: "",
      });
      refetchInvitations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!selectedInvitation) return;
    
    setIsSending(true);
    try {
      // Generate new token and extend expiry
      const newToken = generateRandomString(32);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      // Update the invitation with new token and expiry
      const { error: updateError } = await supabase
        .from("team_invitations")
        .update({ 
          token: newToken,
          expires_at: expiresAt.toISOString()
        })
        .eq("id", selectedInvitation.id);
      
      if (updateError) throw updateError;
      
      const { data: authUser } = await supabase.auth.getUser();
      
      let inviterName = "A team administrator";
      
      if (authUser?.user?.user_metadata) {
        const { first_name, last_name } = authUser.user.user_metadata;
        if (first_name && last_name) {
          inviterName = `${first_name} ${last_name}`;
        }
      }
      
      const invitationLink = `${window.location.origin}/invitation?token=${newToken}`;
      
      await supabase.functions.invoke("send-team-invitation", {
        body: { 
          email: selectedInvitation.email, 
          invitationLink, 
          role: selectedInvitation.role,
          inviterName,
          companyName: "Spotlight Adz"
        },
      });

      toast({
        title: "Success",
        description: "Invitation resent successfully",
      });

      setIsResendingInvitation(false);
      setSelectedInvitation(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend invitation",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!selectedInvitation) return;
    
    try {
      const { error } = await supabase
        .from("team_invitations")
        .update({ status: "rejected" })
        .eq("id", selectedInvitation.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Invitation canceled successfully",
      });

      setIsCancelingInvitation(false);
      setSelectedInvitation(null);
      refetchInvitations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel invitation",
        variant: "destructive",
      });
    }
  };

  const copyInvitationLink = (token: string) => {
    const link = `${window.location.origin}/invitation?token=${token}`;
    navigator.clipboard.writeText(link);
    
    toast({
      title: "Copied",
      description: "Invitation link copied to clipboard",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Team Invitations</h2>
        <Dialog open={isAddingInvitation} onOpenChange={setIsAddingInvitation}>
          <DialogTrigger asChild>
            <Button>
              <MailOpen className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newInvitation.email}
                  onChange={(e) =>
                    setNewInvitation({ ...newInvitation, email: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newInvitation.role}
                  onValueChange={(value: TeamRole) =>
                    setNewInvitation({ ...newInvitation, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={newInvitation.department}
                  onChange={(e) =>
                    setNewInvitation({ ...newInvitation, department: e.target.value })
                  }
                />
              </div>
              <Button 
                className="w-full" 
                onClick={handleAddInvitation}
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {invitations?.filter(inv => inv.status === 'pending').map((invitation) => (
          <Card key={invitation.id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{invitation.email}</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    {invitation.role}
                  </span>
                  {invitation.department && (
                    <span className="text-xs bg-muted px-2 py-1 rounded">
                      {invitation.department}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
            <CardFooter className="bg-muted/50 flex justify-end space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyInvitationLink(invitation.token)}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedInvitation(invitation);
                  setIsResendingInvitation(true);
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedInvitation(invitation);
                  setIsCancelingInvitation(true);
                }}
              >
                <Ban className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        ))}
        
        {invitations?.filter(inv => inv.status === 'pending').length === 0 && (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            No pending invitations. Send an invitation to add team members.
          </div>
        )}
      </div>

      <AlertDialog open={isResendingInvitation} onOpenChange={setIsResendingInvitation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to resend the invitation to {selectedInvitation?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsResendingInvitation(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResendInvitation}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Resend"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isCancelingInvitation} onOpenChange={setIsCancelingInvitation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation to {selectedInvitation?.email}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsCancelingInvitation(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelInvitation}>
              Yes, Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
